import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import PullOutCard from './pull-out-card/pull-out-card';
import PullOutMoreCard from './pull-out-more-card/pull-out-more-card';
import { collectionSerializer } from '@/providers/serializers/content/collection';
import { assessmentSerializer } from '@/providers/serializers/content/assessment';
import { profileAPI } from '@/providers/apis/profile/profile';
import { contentAPI } from '@/providers/apis/content/content';
import { resourceSerializer } from '@/providers/serializers/content/resource';
import { questionSerializer } from '@/providers/serializers/content/question';
import { EXTRACTED, CURATED, TAGEED, COMPUTED } from '@/utils/constants';
import axios from 'axios';
import { courseSerializer } from '@/providers/serializers/content/course';
import { lookupAPI } from '@/providers/apis/lookup/lookup';
import moment from 'moment';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';

@Component({
    name: 'catalog-pull-out',
    components: {
        'material-icon': GoogleMaterialIcon,
        'pull-out': PullOutCard,
        'pull-out-more': PullOutMoreCard,
    },
})

export default class CatalogPullOut extends Vue {

    // ------------------------------------------------------------------
    // Properties

    @Prop()
    private isPullOutLoading!: boolean;

    @Prop()
    private collectionData: any;

    private createdDate: any = null;

    private contentData: any = [];

    private isShowMore: boolean = false;

    private pullOutMoreData: any = null;

    private isLoading: boolean = false;

    private metaData: any = {};

    private dokContent: any = [];

    private readingLevel: any = [];

    private headerMenus: any = [
        {
            name: 'Extracted',
            isActive: true,
            color: '#0072bc',
            keyList: EXTRACTED,
        },
        {
            name: 'Curated',
            isActive: true,
            color: '#28a745',
            keyList: CURATED,
        },
        {
            name: 'Tagged',
            isActive: true,
            color: '#fd7e14',
            keyList: TAGEED,
        },
        {
            name: 'Computed',
            isActive: true,
            color: '#37424b',
            keyList: COMPUTED,
        },
    ];

    // --------------------------------------------------
    // Actions

    /**
     * Use to show more content on pull out
     */
    public onShowMore() {
        this.isShowMore = !this.isShowMore;
        (this.$el as HTMLElement).style.left = 'calc(100vw - 450px)';
        if (this.isShowMore) {
            (this.$el as any).style.left = 0;
        }
    }

    /**
     * Used to close pullout container
     */
    public closeContainer() {
        document.body.style.overflowY = 'auto';
        (this.$el as HTMLElement).style.left = '100vw';
        this.isShowMore = false;
    }

    /**
     * open pullout content using root actions
     */
    public openContainer() {
        (this.$el as HTMLElement).style.left = 'calc(100vw - 450px)';
    }

    public fetchInfo(content: any) {
        const taxonomyId: string = content.taxonomy.id;
        if (!taxonomyId) { return null; }
        const [framework, ...rest] = taxonomyId.split('.');
        const restJoined = rest.join('.');
        let subject: string;
        let code: string = '';
        const match = restJoined.match(/([^]*?)-([^]*)/);
        if (match) {
            subject = match[1];
            code = match[2];
        } else {
            subject = restJoined;
        }
        return {
            framework,
            subject,
            code,
        };
    }

    public async fetchSubjectName(subjectCode: string): Promise<any> {
        const subject = await taxonomyAPI.fetchSubjectById(subjectCode);
        return subject;
    }
    // -----------------------------------------------------------------
    // Hooks
    private created() {
        contentAPI.getMetadataLevel().then((metaData) => {
            this.metaData = metaData;
        });
        this.fetchMetaData();
    }

    @Watch('collectionData', { deep: true })
    private watchingContent(newVal: any, oldVal: any) {
        if (!this.isPullOutLoading && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
            this.collectionData = newVal;
            this.onLoadContent(newVal);
        }

    }

    private onLoadContent(content: any) {
        this.isLoading = true;
        const contentType = content.type;
        if (content.created_at) {
            this.createdDate = moment(content.created_at).format('MMMM YYYY');
        }
        if (contentType === 'collection') {
            if (content.lastModifiedBy || content.creator && content.creator.id) {
                axios.all([
                    this.getUserProfileData(content.lastModifiedBy || content.creator.id),
                    contentAPI.getCollectionById(content.id),
                ]).then(axios.spread((userData, collectionContent) => {
                    this.isLoading = false;
                    this.contentData = collectionContent.content;
                    content.lastModifiedUser = userData[0];
                    this.pullOutMoreData = collectionSerializer
                        .collectionPulloutSerializer(content);
                }));
            }
        } else if (contentType === 'assessment') {
            if (content.lastModifiedBy || content.creator && content.creator.id) {
                axios.all([
                    this.getUserProfileData(content.lastModifiedBy || content.creator.id),
                    contentAPI.getAssessmentById(content.id),
                ]).then(axios.spread((userData, assessmentContent) => {
                    this.isLoading = false;
                    this.contentData = assessmentContent.question;
                    content.lastModifiedUser = userData[0];
                    this.pullOutMoreData = assessmentSerializer
                        .assessmentPulloutSerializer(content);
                }));
            }

        } else if (contentType === 'offline-activity') {
            if (content.lastModifiedBy || content.creator && content.creator.id) {
                axios.all([
                    this.getUserProfileData(content.lastModifiedBy || content.creator.id),
                    contentAPI.getActivityById(content.id),
                ]).then(axios.spread((userData, oaData) => {
                    this.isLoading = false;
                    content.lastModifiedUser = userData[0];
                    this.contentData = oaData.oa_tasks;
                    // Currently we have use collection metadata format if we need any change
                    // We have to create seperate meta data serializer for offline activity
                    this.pullOutMoreData = collectionSerializer
                        .collectionPulloutSerializer(content);
                }));
            }

        } else if (contentType === 'course') {
            if (content.lastModifiedBy || content.creator && content.creator.id) {
                axios.all([
                    this.getUserProfileData(content.lastModifiedBy || content.creator.id),
                    contentAPI.getCourseById(content.id),
                ]).then(axios.spread((userData, courseContent) => {
                    this.isLoading = false;
                    content.lastModifiedUser = userData[0];
                    this.contentData = [];
                    this.pullOutMoreData = courseSerializer
                        .coursePulloutSerializer(content, courseContent);
                }));
            }

        } else if (contentType === 'resource') {
            const payload = {
                resource_ids: [content.id],
                limit: 10,
                skip: 0,
            };
            axios.all([
                contentAPI.getResourceById(content.id),
                contentAPI.fetchSummaryAndTranscript(payload),
            ]).then(axios.spread( (resourceContent, summaryAndTranscript) => {
                this.parseMetaDataLevel(resourceContent);
                resourceContent.owner = content.owner;
                resourceContent.creator = content.creator;
                this.isLoading = false;
                this.contentData = [];
                this.createdDate = moment(resourceContent.created_at).format('MMMM YYYY');
                this.pullOutMoreData = resourceSerializer
                    .resourcePulloutSerializer(resourceContent, content, this.metaData, summaryAndTranscript);
            }));

        } else if (contentType === 'question') {
            contentAPI.getQuestionById(content.id).then((questionData) => {
                this.parseMetaDataLevel(questionData);
                questionData.owner = content.owner;
                questionData.creator = content.creator;
                this.isLoading = false;
                this.contentData = [];
                this.pullOutMoreData = questionSerializer
                    .questionPulloutSerializer(questionData,
                        content,
                        this.metaData);
            });
        } else {
            this.isLoading = false;
        }
    }

    private getUserProfileData(userId: string) {
        return profileAPI.fetchUserProfiles(userId)
            .then((userData) => {
                return userData;
            });
    }

    private parseMetaDataLevel(resourceContent: any) {
        if (resourceContent.metadata) {
            const metadata = resourceContent.metadata;
            const readingLevel = Array.isArray(metadata.reading_level)
                ? metadata.reading_level
                : [metadata.reading_level].filter(Boolean);
            const dok = Array.isArray(metadata.depth_of_knowledge)
                ? metadata.depth_of_knowledge
                : [metadata.depth_of_knowledge].filter(Boolean);
            resourceContent.readingLevel = this.readingLevel.filter(
                (item: any) => readingLevel.indexOf(item.id) !== -1,
            );

            resourceContent.depthofknowledge = this.dokContent.filter(
                (item: any) => dok.indexOf(item.id) !== -1,
            );
        }
    }

    private fetchMetaData() {
        axios.all([
            lookupAPI.readDepthOfKnowledgeItems(),
            lookupAPI.readingLevelItems(),
        ]).then(axios.spread((dok, readingLevel) => {
            this.dokContent = dok;
            this.readingLevel = readingLevel;
        }));
    }
}
